import { CommonModule } from "@angular/common";
import { Component, QueryList, ViewChildren } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule, Router } from "@angular/router";
import {
  AlertController,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonReorder,
  IonReorderGroup,
  IonSearchbar,
  IonTitle,
  IonToolbar,
  ModalController
} from "@ionic/angular/standalone";
import { FavoritePathService, UserFavoritePath } from "../../services/favorite-path.service";

@Component({
  selector: "lib-favorite-list",
  imports: [
    RouterModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonButtons,
    IonList,
    IonReorder,
    IonReorderGroup,
    IonItem,
    IonLabel,
    IonIcon,
    IonInput,
    IonContent,
    IonSearchbar,
    IonBadge
  ],
  templateUrl: "./favorite-list.component.html",
  standalone: true,
  styleUrl: "./favorite-list.component.css",
})
export class FavoriteListComponent {
  items: UserFavoritePath[] = [];
  searchTerm: string = "";

  @ViewChildren(IonInput) editInputs!: QueryList<IonInput>;

  constructor(
    private modalCtrl: ModalController,
    public router: Router,
    private alertController: AlertController,
    private favoritePathService: FavoritePathService,
  ) {
    this.items = this.favoritePathService.getFavorites();
  }

  get filteredItems(): UserFavoritePath[] {
    if (!this.searchTerm.trim()) {
      return this.items;
    }
    const term = this.searchTerm.toLowerCase().trim();
    return this.items.filter((item) => {
      const name = this.displayName(item).toLowerCase();
      const path = (item.Path || item.path || "").toLowerCase();
      return name.includes(term) || path.includes(term);
    });
  }

  doReorder(event: CustomEvent) {
    const from = event.detail.from;
    const to = event.detail.to;

    const movedItem = this.items.splice(from, 1)[0];
    this.items.splice(to, 0, movedItem);

    event.detail.complete();
  }

  async deleteItem(index: number) {
    const item = this.items[index];
    if (!item) {
      return;
    }

    const alert = await this.alertController.create({
      header: "Remove Favorite",
      message: `Are you sure you want to remove "${this.displayName(item)}"?`,
      buttons: [
        {
          text: "Cancel",
          role: "cancel"
        },
        {
          text: "Remove",
          role: "destructive",
          handler: () => this.confirmDeleteItem(item)
        }
      ]
    });

    await alert.present();
  }

  confirmDeleteItem(item: UserFavoritePath) {
    return this.favoritePathService.deleteFavorite(item);
  }

  displayName(item: UserFavoritePath): string {
    return this.favoritePathService.displayName(item);
  }

  displayPath(item: UserFavoritePath): string {
    const rawPath = item.Path || item.path || "";
    if (!rawPath) return "";
    return rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
  }

  getFavoriteIconName(item: UserFavoritePath): string {
    const text = `${this.displayName(item)} ${item.Path || item.path || ""}`.toLowerCase();
    if (text.includes("video") || text.includes("camera") || text.includes("playback") || text.includes("docking")) {
      return "videocam-outline";
    }
    if (text.includes("gate") || text.includes("flight") || text.includes("airport") || text.includes("dock")) {
      return "airplane-outline";
    }
    if (text.includes("fleet") || text.includes("asset") || text.includes("gse") || text.includes("maintenance")) {
      return "car-outline";
    }
    if (text.includes("alarm") || text.includes("alert") || text.includes("warning")) {
      return "warning-outline";
    }
    if (text.includes("trend") || text.includes("tag") || text.includes("chart") || text.includes("insight") || text.includes("report")) {
      return "trending-up-outline";
    }
    if (text.includes("site") || text.includes("organization") || text.includes("building")) {
      return "business-outline";
    }
    return "star-outline";
  }

  getFavoriteIconClass(item: UserFavoritePath): string {
    const text = `${this.displayName(item)} ${item.Path || item.path || ""}`.toLowerCase();
    if (text.includes("video") || text.includes("camera") || text.includes("playback") || text.includes("docking")) {
      return "favorite-icon-wrap--purple";
    }
    if (text.includes("gate") || text.includes("flight") || text.includes("airport") || text.includes("dock")) {
      return "favorite-icon-wrap--blue";
    }
    if (text.includes("fleet") || text.includes("asset") || text.includes("gse") || text.includes("maintenance")) {
      return "favorite-icon-wrap--teal";
    }
    if (text.includes("alarm") || text.includes("alert") || text.includes("warning")) {
      return "favorite-icon-wrap--amber";
    }
    if (text.includes("trend") || text.includes("tag") || text.includes("chart") || text.includes("insight") || text.includes("report")) {
      return "favorite-icon-wrap--green";
    }
    if (text.includes("site") || text.includes("organization") || text.includes("building")) {
      return "favorite-icon-wrap--indigo";
    }
    return "favorite-icon-wrap--blue";
  }

  findItemIndex(item: UserFavoritePath): number {
    return this.items.findIndex((i) => (i.Id != null && i.Id === item.Id) || i === item);
  }

  isAnyEditing(): boolean {
    return this.items.some((item) => item.isEditing);
  }

  toggleEdit(item: UserFavoritePath) {
    if (item.isEditing) {
      this.cancelEdit(item);
      return;
    }

    this.beginEdit(item);
  }

  beginEdit(item: UserFavoritePath) {
    this.items.forEach((favorite) => {
      if (favorite !== item) {
        favorite.isEditing = false;
      }
    });

    item.isEditing = true;
    item.editName = this.displayName(item);
    item.originalEditName = item.editName;

    setTimeout(() => {
      const input = this.editInputs?.first;
      input?.setFocus();
      input?.getInputElement().then((nativeInput) => nativeInput.select());
    });
  }

  cancelEdit(item: UserFavoritePath) {
    item.editName = item.originalEditName ?? this.displayName(item);
    item.isEditing = false;
  }

  updateName(event: any, item: UserFavoritePath) {
    const newName = String(
      event?.detail?.value ?? event?.target?.value ?? event ?? item.editName ?? this.displayName(item)
    ).trim();
    if (!newName) {
      item.editName = this.displayName(item);
      return Promise.resolve();
    }

    return this.favoritePathService.renameFavorite(item, newName);
  }

  saveEdit(item: UserFavoritePath) {
    return this.updateName(item.editName, item);
  }

  onSubmit() {
    // legacy hook
  }

  cancel() {
    return this.modalCtrl.dismiss(null, "cancel");
  }

  confirm() {
    return this.modalCtrl.dismiss(null, "confirm");
  }
}
